import { DEBOUNCE_MS, debounce } from "../core/dom.js";
import { removeRenderedCards } from "../core/render.js";
import type { SlaterList } from "../list.js";
import { MAX_QUERY_LENGTH } from "./config.js";

/**
 * Global site search — what makes a `data-slater-search-list` block different
 * from a CMS list: an idle state while the query is empty or too short, a
 * `?q=`/`?query=` deep link, and query truncation to the server's limit.
 * Loading, chrome and failure handling stay in the shared machine (SlaterList).
 */

export function bindGlobalSearchInput(list: SlaterList): void {
  for (const input of list.scoped("[data-slater-search]")) {
    const run = debounce(() => {
      const raw = (input as HTMLInputElement).value.trim();
      if (raw.length < list.config.minLength) {
        // Below the threshold there is nothing to ask the server ("" 400s).
        list.state.q = "";
        showIdle(list);
        return;
      }
      list.state.q = raw.slice(0, MAX_QUERY_LENGTH);
      list.resetAndLoad();
    }, DEBOUNCE_MS);
    input.addEventListener("input", run);
  }
}

/**
 * The search block's resting state: query empty or below the minimum. Shows
 * the `data-slater-initial` element and clears everything else — including
 * any in-flight request, whose late response must not repopulate the list.
 */
export function showIdle(list: SlaterList): void {
  list.source.abort?.();
  list.rendered = [];
  list.total = 0;
  list.state.visibleCount = list.config.initialLimit;
  // Only the cards — the container may also hold the input and the state
  // elements (it defaults to the wrapper itself).
  removeRenderedCards(list.results);

  const emptyEl = list.emptyEl;
  if (emptyEl) (emptyEl as HTMLElement).style.display = "none";
  const loadMoreEl = list.loadMoreEl;
  if (loadMoreEl) (loadMoreEl as HTMLElement).style.display = "none";
  const countEl = list.countEl;
  if (countEl) countEl.textContent = "";
  const initialEl = list.initialEl;
  if (initialEl) (initialEl as HTMLElement).style.display = "";
}

/**
 * A search block's first load. Deep link: /search-page?q=term searches
 * immediately and pre-fills the inputs; otherwise the block idles — an empty
 * q is not a valid server query. `?query=` is accepted too — it's what
 * Webflow's native Search element submits — with `q` winning when both are
 * present.
 */
export async function initGlobalSearch(list: SlaterList): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  const seed =
    [params.get("q"), params.get("query")]
      .map((v) => v?.trim() ?? "")
      .find((v) => v.length > 0) ?? "";
  if (seed.length >= list.config.minLength) {
    for (const input of list.scoped("[data-slater-search]")) {
      (input as HTMLInputElement).value = seed;
    }
    list.state.q = seed.slice(0, MAX_QUERY_LENGTH);
    await list.load("replace");
  } else {
    showIdle(list);
  }
}
